import { useMemo, useState } from "react";
import { fmtDate } from "@/components/athlete/formatters";
import type { TrendPoint } from "@/components/athlete/types";
import { DataPanelTitle } from "@/components/ui/data-panel";
import { TogglePillGroup, TogglePill } from "@/components/ui/toggle-pills";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

type RankDimension = "ageGroup" | "gender" | "overall";

const RANK_DIMENSIONS: { key: RankDimension; label: string; prefix: string }[] =
  [
    { key: "ageGroup", label: "Age Group", prefix: "AG" },
    { key: "gender", label: "Gender", prefix: "Gender" },
    { key: "overall", label: "Overall", prefix: "Overall" },
  ];

const chartConfig = {
  rank: {
    label: "Rank",
    color: "var(--d8-purple)",
  },
} satisfies ChartConfig;

const getRankValue = (point: TrendPoint, dimension: RankDimension) => {
  switch (dimension) {
    case "ageGroup":
      return point.ageGroupRank;
    case "gender":
      return point.genderRank ?? null;
    case "overall":
      return point.overallRank ?? null;
  }
};

export function RankingTrendPanel({ trend }: { trend: TrendPoint[] }) {
  const [dimension, setDimension] = useState<RankDimension>("ageGroup");

  const dimInfo = RANK_DIMENSIONS.find((d) => d.key === dimension)!;
  const filteredTrend = useMemo(
    () => trend.filter((point) => getRankValue(point, dimension) != null),
    [trend, dimension],
  );

  const chartData = useMemo(() => {
    const lastIdx = filteredTrend.length - 1;
    const step =
      filteredTrend.length > 5
        ? Math.max(1, Math.floor(filteredTrend.length / 4))
        : 1;

    return filteredTrend.map((point, index) => {
      const showLabel =
        filteredTrend.length <= 5 ||
        index === 0 ||
        index === lastIdx ||
        index % step === 0;
      return {
        ...point,
        rank: getRankValue(point, dimension)!,
        xLabel: showLabel
          ? String(
              new Date(`${point.eventDate}T00:00:00Z`).getUTCFullYear(),
            )
          : "",
      };
    });
  }, [filteredTrend, dimension]);

  const maxRank = useMemo(() => {
    if (chartData.length === 0) return 2;
    return Math.max(2, ...chartData.map((point) => point.rank));
  }, [chartData]);

  return (
    <>
      <div className="athlete-trend-header">
        <DataPanelTitle>Ranking Over Time</DataPanelTitle>
        <TogglePillGroup>
          {RANK_DIMENSIONS.map((rankDimension) => (
            <TogglePill
              key={rankDimension.key}
              active={dimension === rankDimension.key}
              onClick={() => setDimension(rankDimension.key)}
            >
              {rankDimension.label}
            </TogglePill>
          ))}
        </TogglePillGroup>
      </div>

      {chartData.length === 0 ? (
        <div className="athlete-trend-empty">
          No ranking data available for this dimension
        </div>
      ) : (
        <ChartContainer
          config={chartConfig}
          className="mt-2 aspect-auto! h-[350px] min-h-[350px] md:h-[400px] md:min-h-[400px]"
        >
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 12, left: 10, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="xLabel"
              tickLine={false}
              axisLine={false}
              minTickGap={24}
              interval={0}
            />
            <YAxis
              dataKey="rank"
              type="number"
              domain={[1, maxRank]}
              reversed
              tickFormatter={(value) => `#${value}`}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            <ChartTooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={
                <ChartTooltipContent
                  labelFormatter={(_label, payload) =>
                    (
                      payload?.[0]?.payload as
                        | { eventName?: string }
                        | undefined
                    )?.eventName ?? "Event"
                  }
                  formatter={(value, _name, item) => {
                    const row = item.payload as (typeof chartData)[number];
                    return `${dimInfo.prefix} #${value}${row.ageGroup ? ` · ${row.ageGroup}` : ""} · ${fmtDate(row.eventDate)}`;
                  }}
                />
              }
            />
            <Line
              dataKey="rank"
              type="monotone"
              stroke="var(--color-rank)"
              strokeWidth={2.25}
              dot={{ r: 3.5, fill: "var(--color-rank)", strokeWidth: 2 }}
              activeDot={{ r: 5, fill: "var(--color-rank)", strokeWidth: 2 }}
            />
          </LineChart>
        </ChartContainer>
      )}
    </>
  );
}
